/**
 * SegmentPrimaryFilter - Primary filter dropdown for segment nodes
 *
 * Mirrors the EditAutomationSegmentPrimaryFilter component but adapted for
 * use within React Flow canvas nodes. Provides "IF" dropdown with filter
 * type options and autocomplete search for tag selection.
 *
 * When the automation trigger is a Policy Renewal trigger, the dropdown
 * offers a "this policy renewing is" option that renders a PolicySelector
 * with hierarchical line-of-business checkboxes (multi-select).
 *
 * Uses components-v2 Dropdown and Pill for consistent design.
 */

import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { IAutoCompleteSearchFieldComponent } from '../../../../components/autocomplete/AutoCompleteSearchField';
import {
	Dropdown,
	DropdownSelectTrigger,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
} from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { PolicySelector } from '../../../../components/email/PolicySelector';
import { useAllClassifiedContactPolicies } from '../../../../../queries/Contact';
import { segmentFilterStyles } from './segmentFilterStyles';
import { SegmentStageFilter } from './SegmentStageFilter';

const OPPORTUNITY_STAGE_OPTION_ID = 'opportunity-stage';

/**
 * Extract selected policy values from a filter criteria object.
 * Ported from EditAutomationTemplateSegmentHeader/presentation.tsx.
 */
function getSelectedPoliciesFromFilterRequest(filterRequest?: Api.IContactFilterCriteria): string[] {
	if (!filterRequest) {
		return [];
	}
	const onlyOneValueSelected = !filterRequest.criteria || !filterRequest.criteria?.length;

	if (filterRequest?.value && onlyOneValueSelected) {
		return [filterRequest.value];
	} else if (
		filterRequest.criteria?.length &&
		filterRequest.criteria.every(y => y.property === Api.ContactFilterCriteriaProperty.Policy)
	) {
		return filterRequest.criteria.filter(x => x.value !== undefined).map(x => x.value);
	}
	return [];
}

/**
 * Filter option definition for the primary filter dropdown.
 * Uses string id for Dropdown radio group value binding.
 */
export interface FilterOption {
	id: string;
	text: string;
	dataContext: Api.IContactFilterCriteria | null;
}

/**
 * Get select options for the primary filter dropdown.
 * Options vary based on whether this is a policy renewal trigger, opportunity-created trigger, or default segment.
 */
export const getPrimaryFilterOptions = (
	isPolicyRenewalTrigger = false,
	isDefaultSegment = false,
	isOpportunityCreatedTrigger = false
): FilterOption[] => {
	const options: FilterOption[] = [];

	if (isOpportunityCreatedTrigger) {
		options.push({
			dataContext: null,
			id: OPPORTUNITY_STAGE_OPTION_ID,
			text: 'is on this opportunity stage',
		});
	}

	if (isPolicyRenewalTrigger) {
		options.push({
			dataContext: {
				criteria: [],
				op: Api.FilterOperator.Or,
				property: Api.ContactFilterCriteriaProperty.Policy,
			},
			id: Api.ResourceSelectorId.PolicyRenew,
			text: 'this policy renewing is',
		});
	}

	options.push({
		dataContext: {
			op: Api.FilterOperator.And,
			property: Api.ContactFilterCriteriaProperty.Tag,
		},
		id: 'tag-and',
		text: 'contact has this tag',
	});

	options.push({
		dataContext: {
			op: Api.FilterOperator.Not,
			property: Api.ContactFilterCriteriaProperty.Tag,
		},
		id: 'tag-not',
		text: 'contact is missing this tag',
	});

	if (isDefaultSegment) {
		options.push({
			dataContext: null,
			id: 'default-case',
			text: 'contact is not in a segment (everyone else)',
		});
	}

	return options;
};

export function resolveSelectedOption(options: FilterOption[], criteria?: Api.IContactFilterCriteria): FilterOption {
	const fallback: FilterOption =
		options[0] ||
		({
			id: 'tag-and',
			text: 'contact has this tag',
			dataContext: {
				op: Api.FilterOperator.And,
				property: Api.ContactFilterCriteriaProperty.Tag,
			},
		} as FilterOption);

	if (!criteria) {
		const defaultCaseOption = options.find(x => x.id === 'default-case');
		if (defaultCaseOption) {
			return defaultCaseOption;
		}
	}

	const exactMatch = options.find(
		x => x.dataContext?.property === criteria?.property && (x.dataContext?.op === criteria?.op || !criteria?.op)
	);
	if (exactMatch) {
		return exactMatch;
	}

	if (criteria?.property === Api.ContactFilterCriteriaProperty.Tag) {
		const tagOption = options.find(x => x.dataContext?.property === Api.ContactFilterCriteriaProperty.Tag);
		if (tagOption) {
			return tagOption;
		}
	}

	if (criteria?.property === Api.ContactFilterCriteriaProperty.Policy) {
		const policyOption = options.find(x => x.dataContext?.property === Api.ContactFilterCriteriaProperty.Policy);
		if (policyOption) {
			return policyOption;
		}
	}

	return (
		(criteria?.op && !criteria?.property ? options.find(x => x.dataContext?.op === criteria?.op) : undefined) ||
		options.find(x => x.dataContext === null) ||
		fallback
	);
}

export function getPrimaryFilterAutocompleteType({
	criteria,
	selectedOption,
	isPolicyRenewalTrigger,
}: {
	criteria?: Api.IContactFilterCriteria;
	selectedOption?: FilterOption;
	isPolicyRenewalTrigger: boolean;
}) {
	const policySelected =
		isPolicyRenewalTrigger &&
		(criteria?.property === Api.ContactFilterCriteriaProperty.Policy ||
			selectedOption?.id === Api.ResourceSelectorId.PolicyRenew);
	return policySelected
		? Api.ResourceAutoCompleteViewModelType.LineOfBusiness
		: Api.ResourceAutoCompleteViewModelType.Tag;
}

interface ISegmentPrimaryFilterProps {
	/** Current filter criteria */
	criteria?: Api.IContactFilterCriteria;
	/** Callback when filter criteria changes */
	onCriteriaChanged?(criteria?: Api.IContactFilterCriteria): void;
	/** Whether this is the default segment */
	isDefault: boolean;
	/** Whether the filter is read-only */
	isReadOnly?: boolean;
	/** Whether this trigger supports policy-first filtering (shows policy filter option) */
	isPolicyRenewalTrigger?: boolean;
	/** Whether this trigger is OpportunityCreatedAutomationTrigger (enables stage gating option) */
	isOpportunityCreatedTrigger?: boolean;
	/** Live trigger payload — used by the stage filter to resolve the boardId for stage loading. */
	trigger?: Api.IAutomationTrigger;
	/** Currently selected opportunity stage IDs (only honored when isOpportunityCreatedTrigger). */
	stageIds?: string[];
	/** Callback when the selected opportunity stages change. Pass [] to clear. */
	onStageIdsChanged?(stageIds: string[]): void;
	/** Impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
	/** Optional additional styles for the container */
	styles?: StyleDeclarationValue[];
}

export const SegmentPrimaryFilter: React.FC<ISegmentPrimaryFilterProps> = props => {
	const {
		criteria,
		onCriteriaChanged,
		isDefault,
		isReadOnly,
		isPolicyRenewalTrigger = false,
		isOpportunityCreatedTrigger = false,
		trigger,
		stageIds,
		onStageIdsChanged,
		impersonationContext,
		styles = [],
	} = props;

	const autocompleteComponentRef = React.useRef<IAutoCompleteSearchFieldComponent>(null);
	const onAutocompleteComponentRef = React.useCallback((ref?: IAutoCompleteSearchFieldComponent) => {
		autocompleteComponentRef.current = ref;
	}, []);
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

	// Guard: skip blur save when an item was just selected (blur fires immediately after selection
	// and would overwrite the saved value with an empty string).
	const justSelectedRef = React.useRef(false);

	const options = React.useMemo(
		() => getPrimaryFilterOptions(isPolicyRenewalTrigger, isDefault, isOpportunityCreatedTrigger),
		[isPolicyRenewalTrigger, isDefault, isOpportunityCreatedTrigger]
	);

	// Internal mode flag — disambiguates "stage option active" from "default-case active" when
	// both have a null dataContext. New opportunity-created segments should start in stage mode.
	const shouldDefaultToStageMode =
		isOpportunityCreatedTrigger && !isDefault && (!criteria || (stageIds?.length ?? 0) > 0);
	const [isStageMode, setIsStageMode] = React.useState<boolean>(() => shouldDefaultToStageMode);

	React.useEffect(() => {
		if (shouldDefaultToStageMode) {
			setIsStageMode(true);
		} else if (!isOpportunityCreatedTrigger || isDefault) {
			setIsStageMode(false);
		}
	}, [shouldDefaultToStageMode, isOpportunityCreatedTrigger, isDefault]);

	const selectedOption = React.useMemo(() => {
		if (isStageMode && isOpportunityCreatedTrigger) {
			const stageOpt = options.find(o => o.id === OPPORTUNITY_STAGE_OPTION_ID);
			if (stageOpt) {
				return stageOpt;
			}
		}
		return resolveSelectedOption(options, criteria);
	}, [options, criteria, isStageMode, isOpportunityCreatedTrigger]);

	// Determine whether to show PolicySelector or tag autocomplete.
	// Uses canonical criteria first to avoid stale-dropdown UI mode drift.
	const autocompleteType = getPrimaryFilterAutocompleteType({
		criteria,
		selectedOption,
		isPolicyRenewalTrigger,
	});

	// Extract currently selected policies from criteria for PolicySelector
	const selectedPolicies = React.useMemo(() => getSelectedPoliciesFromFilterRequest(criteria), [criteria]);
	const [draftPolicies, setDraftPolicies] = React.useState<string[]>(selectedPolicies);
	React.useEffect(() => {
		setDraftPolicies(selectedPolicies);
	}, [selectedPolicies]);

	// Fetch classified policies for PolicySelector (only used when policy filter is active)
	const { data: allClassifiedPolicies, isPending: isLoadingAllClassifiedPolicies } = useAllClassifiedContactPolicies({
		contactsRequest: { criteria: [] },
		enabled: autocompleteType === Api.ResourceAutoCompleteViewModelType.LineOfBusiness,
		refetchOnWindowFocus: false,
		impersonationContext,
	});

	// Handle dropdown option change (v2 Dropdown radio group)
	const handleOptionChange = React.useCallback(
		(newValue: string) => {
			const option = options.find(o => o.id === newValue);
			if (!option) {
				return;
			}

			if (option.id === OPPORTUNITY_STAGE_OPTION_ID) {
				// Entering stage mode is purely local — no save. The first stageIds selection
				// will fire the unified PUT (filter + stageIds together). If the user never picks
				// a stage and switches to another option, that switch fires the unified criteria
				// PUT that also clears stageIds. Avoids a transient invalid case state.
				setIsStageMode(true);
				autocompleteComponentRef.current?.clearInput();
				setDraftPolicies([]);
				setIsDropdownOpen(false);
				return;
			}

			// Switching away from stage mode — `handlePrimaryCriteriaChanged` in SegmentNode
			// will atomically clear stageIds alongside the new filter in a single PUT.
			if (isStageMode) {
				setIsStageMode(false);
			}

			const propertyChanged = option.dataContext?.property !== criteria?.property;
			const opChanged = option.dataContext?.op !== criteria?.op;
			if (propertyChanged || opChanged) {
				autocompleteComponentRef.current?.clearInput();
			}

			if (option.id !== Api.ResourceSelectorId.PolicyRenew) {
				setDraftPolicies([]);
			}

			onCriteriaChanged?.(option.dataContext ? { ...option.dataContext } : null);
			setIsDropdownOpen(false);
		},
		[options, onCriteriaChanged, criteria, isStageMode]
	);

	const onSearchFieldItemSelected = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>) => {
			if (!e.selection || typeof e.selection !== 'string') {
				return;
			}

			const value = e.selection;
			if (e.target) {
				e.target.setSearchQuery(value);
			}

			// Prevent the subsequent blur event from overwriting the saved value.
			justSelectedRef.current = true;
			setTimeout(() => {
				justSelectedRef.current = false;
			}, 100);

			onCriteriaChanged?.({
				...(selectedOption?.dataContext || {}),
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: value || '',
			});
		},
		[onCriteriaChanged, selectedOption?.dataContext]
	);

	const handleClearTag = React.useCallback(() => {
		autocompleteComponentRef.current?.clearInput();
		onCriteriaChanged?.({
			...(selectedOption?.dataContext || {}),
			property: Api.ContactFilterCriteriaProperty.Tag,
			value: '',
		});
	}, [onCriteriaChanged, selectedOption?.dataContext]);

	const onSearchFieldBlur = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.FocusEvent<HTMLInputElement>>) => {
			// Skip blur save when an item was just selected — the selection handler already persisted.
			if (justSelectedRef.current) {
				return;
			}
			onCriteriaChanged?.({
				...(selectedOption?.dataContext || {}),
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: e.sourceEvent?.target?.value || '',
			});
		},
		[onCriteriaChanged, selectedOption?.dataContext]
	);

	const onCreateAutoCompleteViewModel = React.useCallback(
		(_type: Api.ResourceAutoCompleteViewModelType, suggestedVm: Api.ResourceAutoCompleteViewModel) => {
			return impersonationContext ? suggestedVm.impersonate(impersonationContext) : suggestedVm;
		},
		[impersonationContext]
	);

	// Policy changes are staged locally while the dropdown is open, then persisted on close/blur.
	const onRenewalPoliciesChanged = React.useCallback((policies: string[]) => {
		setDraftPolicies(policies);
	}, []);

	const onRenewalPoliciesCommitted = React.useCallback(
		(policies: string[]) => {
			setDraftPolicies(policies);
			const nextRequest: Api.IContactFilterCriteria = policies.length
				? {
						criteria: policies.map(x => ({
							property: Api.ContactFilterCriteriaProperty.Policy,
							value: x,
						})),
						property: Api.ContactFilterCriteriaProperty.Policy,
						op: Api.FilterOperator.Or,
					}
				: {
						criteria: [],
						property: Api.ContactFilterCriteriaProperty.Policy,
						op: Api.FilterOperator.Or,
					};
			onCriteriaChanged?.(nextRequest);
		},
		[onCriteriaChanged]
	);

	const placeholder =
		autocompleteType === Api.ResourceAutoCompleteViewModelType.LineOfBusiness ? 'Search policy' : 'Search tags';

	// Default segment just shows the text
	if (isDefault) {
		return (
			<div className={`${css(segmentFilterStyles.primaryFilterContainer, ...styles)} nodrag nopan`}>
				<span className={css(segmentFilterStyles.ifLabel)}>IF</span>
				<div className={css(segmentFilterStyles.defaultText)}>{selectedOption?.text}</div>
			</div>
		);
	}

	return (
		<div className={`${css(segmentFilterStyles.primaryFilterContainer, ...styles)} nodrag nopan`}>
			<span className={css(segmentFilterStyles.ifLabel)}>IF</span>
			<Dropdown open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
				<DropdownSelectTrigger disabled={isReadOnly} styles={[segmentFilterStyles.filterDropdownTrigger]}>
					{selectedOption.text}
				</DropdownSelectTrigger>
				<DropdownContent align='start' styles={[segmentFilterStyles.filterDropdownContent]}>
					<DropdownRadioGroup value={selectedOption.id} onValueChange={handleOptionChange}>
						{options.map(opt => (
							<DropdownRadioItem key={opt.id} value={opt.id} styles={[segmentFilterStyles.filterDropdownItem]}>
								{opt.text}
							</DropdownRadioItem>
						))}
					</DropdownRadioGroup>
				</DropdownContent>
			</Dropdown>

			{/* Value display: stage chip-input, policy selector, tag pill, or search field */}
			{isStageMode ? (
				<div className={`${css(segmentFilterStyles.stageFilterContainer)} nodrag nopan`}>
					<SegmentStageFilter
						trigger={trigger}
						selectedStageIds={stageIds ?? []}
						onSelectedStageIdsChanged={onStageIdsChanged}
						disabled={isReadOnly}
						impersonationContext={impersonationContext}
					/>
				</div>
			) : criteria && autocompleteType === Api.ResourceAutoCompleteViewModelType.LineOfBusiness ? (
				<div className={`${css(segmentFilterStyles.policySelectorContainer)} nodrag nopan`}>
					<PolicySelector
						classifiedPolicies={allClassifiedPolicies}
						isLoadingClassifiedPolicies={isLoadingAllClassifiedPolicies}
						onSelectedPoliciesChanged={onRenewalPoliciesChanged}
						onSelectionCommitted={onRenewalPoliciesCommitted}
						selectedPolicies={draftPolicies}
					/>
				</div>
			) : criteria?.value ? (
				<div className={css(segmentFilterStyles.tagPillContainer)}>
					<Pill variant='tag' color='blue' label={criteria.value} onRemove={!isReadOnly ? handleClearTag : undefined} />
				</div>
			) : criteria ? (
				<SimpleAutoCompleteSearchField
					initialSearchQuery={criteria?.value || undefined}
					onBlur={onSearchFieldBlur}
					onClear={handleClearTag}
					onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
					onItemSelected={onSearchFieldItemSelected}
					onSearchFieldRef={onAutocompleteComponentRef}
					placeholder={placeholder}
					style={[segmentFilterStyles.filterSearchField]}
					type={autocompleteType}
				/>
			) : null}
		</div>
	);
};
